import { Box, Card, Link, Section, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { Cross } from '@factorialco/f0-react/icons/app'
import {
  AtsContextDocument,
  AtsJoinPromotionSettingsDocument,
  CreateJoinCompanyDocument,
  DeleteJoinCompanyDocument,
} from 'generated/resources/graphql'
import { useState } from 'react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ApprovalsBlock from 'modules/approvals/components/ApprovalsConfigurationBlock'
import { useApprovalFeatures } from 'modules/approvals/hooks/useFeatureLimits'
import { useAtsContext } from 'modules/ats/lib/atsContext'

import { ExternalProfiles } from './ExternalProfiles'

export const JoinPromotionSettings = () => {
  const { company } = useCurrentSession()
  const { joinEnabled } = useAtsContext()
  const { canAddGroup } = useApprovalFeatures('purchases')
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false)

  const refetchQueries = [AtsContextDocument, AtsJoinPromotionSettingsDocument]

  const [createIntegrationsJoinCompany, { loading: creatingCompany }] = useMutation(
    CreateJoinCompanyDocument,
    {
      extractorFn: (data) => data.integrationsJoinMutations.createIntegrationsjoinJoinCompany,
      refetchQueries,
    }
  )

  const [deleteIntegrationsJoinCompany, { loading: deletingCompany }] = useMutation(
    DeleteJoinCompanyDocument,
    {
      extractorFn: (data) => data.integrationsJoinMutations.deleteIntegrationsjoinJoinCompany,
      refetchQueries,
    }
  )

  const { data } = useQuery(AtsJoinPromotionSettingsDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const canManageAtsSettings = data?.ats._policies.manageAtsSettings.can ?? false
  const premiumPortalsEnabled =
    data?.features.enabledFeatures.nodes.find(
      (feature) => feature.name === 'dev_ats_join_premium_portals'
    )?.enabled ?? false
  const canSetJoinPurchaseRequestsApprovals = canManageAtsSettings && premiumPortalsEnabled

  const joinCompanyNode = data?.integrationsJoin.joinCompaniesConnection.nodes[0]
  const joinCompanyId = joinCompanyNode?.id
  const isDeactivating = joinCompanyNode?.status === 'deactivating'

  const reactivationDate = joinCompanyNode?.reactivationBlockedUntil
    ? dateBackendDateTimeToUI(joinCompanyNode.reactivationBlockedUntil)
    : null

  const createJoinCompany = async () => {
    const result = await createIntegrationsJoinCompany({
      variables: {
        companyId: company.id,
      },
    })

    const handled = handleResult(result, (errors) => {
      return app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  const deleteJoinCompany = async () => {
    if (!joinCompanyId) return

    const result = await deleteIntegrationsJoinCompany({
      variables: {
        id: joinCompanyId,
      },
    })

    const handled = handleResult(result, (errors) => {
      return app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      setIsDeactivateDialogOpen(false)
    }

    return handled
  }

  const handleJoinInstallation = (toggleValue: boolean) => {
    if (toggleValue === joinEnabled) return

    if (joinEnabled) {
      setIsDeactivateDialogOpen(true)
      return
    }

    createJoinCompany()
  }

  return (
    <>
      <Section
        illustration="network"
        title={i18n.t('ats.settings.job_promotions.title')}
        description={i18n.t('ats.settings.job_promotions.description')}
        banner={
          <HelpCenterLink name="ats_integrations_join_how_to">
            {i18n.t('ats.settings.job_promotions.learn_more.title')}
          </HelpCenterLink>
        }
      >
        <Box gap="s12">
          {isDeactivating ? (
            <F0Alert
              variant="warning"
              title={i18n.t('ats.settings.job_promotions.promote_jobs.deactivating_banner.title')}
              description={i18n.t(
                'ats.settings.job_promotions.promote_jobs.deactivating_banner.description',
                { date: reactivationDate }
              )}
            />
          ) : (
            <Card>
              {/* promote jobs  */}
              <Card.Title
                size="small"
                action={
                  <TogglerCheckbox
                    value={joinEnabled}
                    disabled={creatingCompany || deletingCompany || (joinEnabled && !joinCompanyId)}
                    onChange={handleJoinInstallation}
                  />
                }
              >
                {i18n.t('ats.settings.job_promotions.promote_jobs.title')}
              </Card.Title>
              <Card.Description>
                {i18n.t('ats.settings.job_promotions.promote_jobs.description')}
              </Card.Description>
              <Card.Footer>
                <Box gap="s8">
                  <Link.Href href="https://join.com/privacy-policy" target="_blank" external>
                    JOIN privacy policy
                  </Link.Href>
                </Box>
              </Card.Footer>
            </Card>
          )}
        </Box>
      </Section>
      {joinEnabled && (
        <Box gap="s12">
          {canSetJoinPurchaseRequestsApprovals && (
            <ApprovalsBlock
              category="premium_portals"
              title={i18n.t('ats.settings.job_promotions.purchase_requests.title')}
              description={i18n.t('ats.settings.job_promotions.purchase_requests.description')}
              multigroup
              icon={'document-approved'}
              hideAddGroupButton={!joinEnabled && !canAddGroup()}
              helpCenterUrlName="multiportal-integration"
            />
          )}
          <ExternalProfiles />
        </Box>
      )}
      {isDeactivateDialogOpen && (
        <Dialog
          open
          onClose={() => setIsDeactivateDialogOpen(false)}
          header={{
            type: 'critical',
            title: i18n.t('ats.settings.job_promotions.promote_jobs.deactivate_dialog.title'),
            description: i18n.t(
              'ats.settings.job_promotions.promote_jobs.deactivate_dialog.content'
            ),
          }}
          actions={{
            primary: {
              icon: Cross,
              label: i18n.t('ats.settings.job_promotions.promote_jobs.deactivate_dialog.confirm'),
              onClick: deleteJoinCompany,
              variant: 'critical',
              disabled: deletingCompany,
            },
            secondary: {
              label: i18n.t('common.cancel'),
              onClick: () => setIsDeactivateDialogOpen(false),
            },
          }}
        />
      )}
    </>
  )
}
